// src/services/auth-manager.service.js

import EventEmitter from 'events';
import customOAuthService from './custom-oauth.service';
import auth0Service from './auth0.service';
import { isJwtExpired } from '../util/oauth-utils';

// Auth provider types
export const AUTH_PROVIDERS = {
  CUSTOM_OAUTH: 'custom_oauth',
  ASURITE: 'asurite'
};

class AuthManager extends EventEmitter {
  constructor() {
    super();
    this.providers = new Map();
    this.activeProvider = null;
    this.activeProviderType = null;

    // Register available auth providers
    this.registerProvider(AUTH_PROVIDERS.CUSTOM_OAUTH, customOAuthService);
    this.registerProvider(AUTH_PROVIDERS.ASURITE, auth0Service);

    // Restore active provider from localStorage
    this.restoreActiveProvider();

    // Forward events from active provider
    this.setupEventForwarding();
  }

  // Register an auth provider
  registerProvider(type, provider) {
    this.providers.set(type, provider);
  }

  // Get available auth provider types
  getAvailableProviders() {
    return Array.from(this.providers.keys());
  }

  // Set active auth provider
  setActiveProvider(providerType) {
    if (!this.providers.has(providerType)) {
      throw new Error(`Auth provider ${providerType} not registered`);
    }

    this.activeProviderType = providerType;
    this.activeProvider = this.providers.get(providerType);

    // Store in localStorage for persistence
    localStorage.setItem('activeAuthProvider', providerType);

    // Setup event forwarding for new provider
    this.setupEventForwarding();

    return this.activeProvider;
  }

  // Get current active provider
  getActiveProvider() {
    return this.activeProvider;
  }

  // Get current active provider type
  getActiveProviderType() {
    return this.activeProviderType;
  }

  // Restore active provider from localStorage
  restoreActiveProvider() {
    const savedProviderType = localStorage.getItem('activeAuthProvider');
    if (savedProviderType && this.providers.has(savedProviderType)) {
      this.setActiveProvider(savedProviderType);
    } else {
      // Default to first available provider
      const firstProvider = this.providers.keys().next().value;
      if (firstProvider) {
        this.setActiveProvider(firstProvider);
      }
    }
  }

  // Setup event forwarding from active provider
  setupEventForwarding() {
    if (this.activeProvider && typeof this.activeProvider.on === 'function') {
      // Remove existing listeners to avoid duplicates
      this.activeProvider.removeAllListeners('loginEvent');

      // Forward login events
      this.activeProvider.on('loginEvent', (data) => {
        this.emit('loginEvent', {
          ...data,
          providerType: this.activeProviderType
        });
      });
    }
  }

  // Login with specific provider
  login(providerType = null, options = {}) {
    const provider = providerType ? this.setActiveProvider(providerType) : this.activeProvider;

    if (!provider) {
      throw new Error('No auth provider available');
    }

    // Auto-extract course_offering_id to preserve during auth flow
    const enhancedOptions = this._enhanceOptionsWithCourseOfferingId(options);

    return provider.login(enhancedOptions);
  }

  // Enhanced options with course_offering_id preservation logic
  _enhanceOptionsWithCourseOfferingId(options = {}) {
    // Don't override if already provided
    if (options.course_offering_id) {
      return options;
    }

    // Try to get course_offering_id from current URL
    const queryParams = new URLSearchParams(window.location.search);
    let courseOfferingId = queryParams.get('course_offering_id');

    // If not in current URL, try from stored requestedURL
    if (!courseOfferingId) {
      const requestedURL = localStorage.getItem('requestedURL');
      if (requestedURL) {
        try {
          const urlParts = requestedURL.split('?');
          if (urlParts.length > 1) {
            const requestedParams = new URLSearchParams(urlParts[1]);
            courseOfferingId = requestedParams.get('course_offering_id');
          }
        } catch (error) {
          // Failed to parse requestedURL from localStorage - silently continue
        }
      }
    }

    // If found, add to options
    if (courseOfferingId) {
      const enhancedOptions = {
        ...options,
        course_offering_id: courseOfferingId
      };
      return enhancedOptions;
    }

    return options;
  }

  // Silent authentication with active provider
  silentAuth() {
    if (!this.activeProvider) {
      return Promise.reject(new Error('No active auth provider'));
    }

    return this.activeProvider.silentAuth();
  }

  // Parse callback access token - auto-detect provider based on URL
  parseCallbackAccessToken() {
    // Don't process logout URLs as OAuth callbacks
    if (window.location.pathname === '/logout' || window.location.pathname === '/logout/') {
      return Promise.reject(new Error('Logout URLs should not be processed as OAuth callbacks'));
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlHash = window.location.hash;

    // Auto-detect provider based on callback URL parameters
    let detectedProvider = null;

    // Check for Auth0/Cognito callback (has access_token and id_token in hash)
    if (urlHash && urlHash.includes('access_token') && urlHash.includes('id_token')) {
      detectedProvider = this.providers.get(AUTH_PROVIDERS.ASURITE);
      this.setActiveProvider(AUTH_PROVIDERS.ASURITE);
    }
    // Check for custom OAuth callback (has 'code' parameter)
    else if (urlParams.get('code')) {
      detectedProvider = this.providers.get(AUTH_PROVIDERS.CUSTOM_OAUTH);
      this.setActiveProvider(AUTH_PROVIDERS.CUSTOM_OAUTH);
    }
    // Fallback to active provider
    else {
      detectedProvider = this.activeProvider;
    }

    if (!detectedProvider) {
      return Promise.reject(new Error('No auth provider available to handle callback'));
    }

    if (typeof detectedProvider.parseCallbackAccessToken !== 'function') {
      return Promise.reject(new Error('Detected provider does not support callback parsing'));
    }

    return detectedProvider.parseCallbackAccessToken();
  }

  // Check if user is authenticated with active provider
  isAuthenticated() {
    if (!this.activeProvider) {
      return false;
    }

    return this.activeProvider.isAuthenticated();
  }

  // Get user ID from active provider
  getUserId() {
    if (!this.activeProvider) {
      return null;
    }

    // Try different methods based on provider type
    if (typeof this.activeProvider.getUserId === 'function') {
      return this.activeProvider.getUserId();
    }

    if (typeof this.activeProvider.getAsuRiteIdFromToken === 'function') {
      return this.activeProvider.getAsuRiteIdFromToken();
    }

    return null;
  }

  // Get username from active provider
  getUsername() {
    if (!this.activeProvider) {
      return null;
    }

    // Try different methods based on provider type
    if (typeof this.activeProvider.getUsername === 'function') {
      return this.activeProvider.getUsername();
    }

    if (typeof this.activeProvider.getAsurite === 'function') {
      return this.activeProvider.getAsurite();
    }

    return null;
  }

  // Get user email from active provider
  getUserEmail() {
    if (!this.activeProvider || typeof this.activeProvider.getUserEmail !== 'function') {
      return null;
    }

    return this.activeProvider.getUserEmail();
  }

  // Get access token from active provider
  getAccessToken() {
    if (!this.activeProvider) {
      return null;
    }

    if (typeof this.activeProvider.getAccessToken === 'function') {
      return this.activeProvider.getAccessToken();
    }

    if (this.activeProvider.accessToken) {
      return this.activeProvider.accessToken;
    }

    return null;
  }

  // Get ID token from active provider
  getIdToken() {
    if (!this.activeProvider || typeof this.activeProvider.getIdToken !== 'function') {
      return null;
    }

    return this.activeProvider.getIdToken();
  }

  // Return the current ASURITE (Cognito) ID token as a raw JWT string, for the
  // backend-to-backend exchange (eaulc api-hub -> aether). auth0Service stores it
  // as a CognitoIdToken object (not a raw string), so normalize + guard the shape.
  getAsuriteIdToken() {
    const rawIdToken =
      (typeof auth0Service.getIdToken === 'function' && auth0Service.getIdToken()) || auth0Service.idToken;
    let idToken = null;
    if (typeof rawIdToken === 'string') {
      idToken = rawIdToken;
    } else if (rawIdToken) {
      idToken =
        (typeof rawIdToken.getJwtToken === 'function' && rawIdToken.getJwtToken()) ||
        rawIdToken.jwtToken ||
        rawIdToken.idToken ||
        null;
    }
    if (!idToken || typeof idToken !== 'string' || idToken.split('.').length !== 3) {
      const err = new Error('No valid ASURITE Cognito ID token available for the aether exchange');
      err.type = 'ASURITE_NO_ID_TOKEN';
      throw err;
    }
    // ULC-3226: Cognito ID tokens live 1h and the implicit flow mints no refresh
    // token, so a restored session hands over a long-dead JWT aether will reject.
    if (isJwtExpired(idToken)) {
      const err = new Error('ASURITE Cognito ID token is expired; a fresh Cognito authorize is required');
      err.type = 'ASURITE_ID_TOKEN_EXPIRED';
      throw err;
    }
    return idToken;
  }

  // Get user profile from active provider
  getProfile() {
    if (!this.activeProvider || typeof this.activeProvider.getProfile !== 'function') {
      return null;
    }

    return this.activeProvider.getProfile();
  }

  // Logout from active provider
  async logOut() {
    if (!this.activeProvider) {
      return;
    }

    // Store provider type temporarily for post-logout callback handling
    const providerType = this.activeProviderType;

    if (typeof this.activeProvider.logOut === 'function') {
      await this.activeProvider.logOut();
    }

    // Clear active provider from localStorage after logout
    localStorage.removeItem('activeAuthProvider');

    // Reset active provider
    this.activeProvider = null;
    this.activeProviderType = null;

    // Store provider type temporarily in sessionStorage for post-logout callback
    if (providerType) {
      sessionStorage.setItem('logout_provider_type', providerType);
    }
  }

  // Handle post-logout callback
  handlePostLogoutCallback() {
    // Get the stored provider type from sessionStorage (set during logout)
    const savedProviderType = sessionStorage.getItem('logout_provider_type');

    if (!savedProviderType) {
      return true; // No provider stored, logout is complete
    }

    const provider = this.providers.get(savedProviderType);
    if (!provider) {
      // Clean up and assume logout is complete
      sessionStorage.removeItem('logout_provider_type');
      return true;
    }

    let result = true;
    if (typeof provider.handlePostLogoutCallback === 'function') {
      result = provider.handlePostLogoutCallback();
    }

    // Clean up session storage
    sessionStorage.removeItem('logout_provider_type');

    return result;
  }

  // Legacy compatibility methods
  getAsurite() {
    return this.getUsername();
  }

  getAsuRiteIdFromToken() {
    return this.getUserId();
  }

  // Event listener methods for compatibility
  addListener(event, listener) {
    this.on(event, listener);
  }

  removeListener(event, listener) {
    // Use off() if available (Node.js 10+), otherwise use removeListener()
    if (typeof this.off === 'function') {
      this.off(event, listener);
    } else {
      // Fallback to the inherited removeListener method from EventEmitter
      super.removeListener(event, listener);
    }
  }
}

export default new AuthManager();
